import {
  Select,
  SelectProps,
  Stack,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
} from "@mantine/core"
import type { FileWithPath } from "@mantine/dropzone"
import { DEFAULT_MIME_TYPES, DocumentUploader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { FindCertificatesResponseDto } from "~/gen/api"
import { useFindCertificates } from "~/gen/api/hooks/useFindCertificates"
import { CertificateViewer } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateViewer"
import { useUploadMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useUploadMutation"

interface Props extends Partial<SelectProps> {
  productionSiteId: string
  typeId: string
  certificateId: string | null
  onCertificateSelected: (certificateId: string | null) => void
  disabled?: boolean
}

export const CertificatesSelect = ({
  productionSiteId,
  certificateId,
  onCertificateSelected,
  disabled,
  typeId,
}: Props) => {
  const { t } = useTranslation("traceability")
  const { data: certificates } = useFindCertificates({
    productionSiteId,
    typeId,
  })

  const selectOptions = certificates?.data?.map((certificate) => ({
    value: certificate.id,
    label: certificate.reference
      ? `${certificate.type.name}: ${certificate.reference}`
      : certificate.type.name,
    certificate,
  }))

  const { mutate: uploadCertificate, isPending: isUploadingCertificate } =
    useUploadMutation({
      onSuccess: (data) => {
        onCertificateSelected(data)
      },
    })

  const handleDrop = (files: FileWithPath[]) => {
    if (!files[0] || !productionSiteId || !typeId) {
      return
    }
    uploadCertificate({
      file: files[0],
      productionSiteId,
      typeId,
    })
  }

  const certificate = certificates?.data.find(
    (certificate) => certificate.id === certificateId
  )
  if (disabled && certificate) {
    return <CertificateViewer certificate={certificate} />
  }

  return (
    <Stack>
      <Select
        data={selectOptions}
        value={certificateId}
        searchable
        clearable
        withCheckIcon
        disabled={disabled}
        onChange={(value) => {
          onCertificateSelected(value)
        }}
        renderOption={({
          option,
        }: ComboboxLikeRenderOptionInput<
          ComboboxItem & { certificate?: FindCertificatesResponseDto }
        >) => {
          if (!option.certificate) {
            return option.label
          }
          return <CertificateViewer certificate={option.certificate} />
        }}
      />
      {!disabled && !certificateId && (
        <DocumentUploader
          title={t("certificates.upload.title")}
          onDrop={handleDrop}
          isLoading={isUploadingCertificate}
          maxSize={100 * 1024 ** 2}
          accept={[...DEFAULT_MIME_TYPES]}
          width="100%"
        />
      )}
    </Stack>
  )
}
